import { RuleSeverity } from 'types/dt-graphql-apollo-v2';
import {
  RuleOperator,
  RuleOperatorLabel,
  ConditionCheckView,
  ConditionOperator,
} from '../DataConsistencyExplorer/types';
import type { ReportStatus } from '../consistencyAggregation';

export type { ConditionCheckView };

export { RuleOperatorLabel };

// Reports (view, download, export) exclude disabled rules — their mappers drop
// any rule where `isRuleDisabled(tags)` is true. Re-exported from the consistency
// explorer helpers so the predicate has a single source of truth.
export { isRuleDisabled } from '../DataConsistencyExplorer/helpers';

const RULE_OPERATOR_VALUES = new Set<string>(Object.values(RuleOperator));
const SEVERITY_VALUES = new Set<string>(Object.values(RuleSeverity));
const CONDITION_OPERATOR_VALUES = new Set<string>(
  Object.values(ConditionOperator)
);

const isRuleOperator = (value: string): value is RuleOperator =>
  RULE_OPERATOR_VALUES.has(value);

const isRuleSeverity = (value: string): value is RuleSeverity =>
  SEVERITY_VALUES.has(value);

export const toRuleOperator = (
  value: string | null | undefined
): RuleOperator =>
  value && isRuleOperator(value) ? value : RuleOperator.IN_SET;

export const toRuleSeverity = (
  value: string | null | undefined
): RuleSeverity => (value && isRuleSeverity(value) ? value : RuleSeverity.None);

const isConditionOperator = (value: string): value is ConditionOperator =>
  CONDITION_OPERATOR_VALUES.has(value);

export const toConditionOperator = (
  value: string | null | undefined
): ConditionOperator =>
  value && isConditionOperator(value) ? value : ConditionOperator.EQUALS;

export const mapConditionChecks = (
  items:
    | Array<{
        externalId: string;
        sourceAttribute: string;
        operator?: string | null;
        targetAttribute: string;
        isLiteralValue?: boolean | null;
        useTargetSchema?: boolean | null;
      } | null>
    | null
    | undefined
): ConditionCheckView[] =>
  (items ?? []).flatMap((cc) => {
    if (!cc) return [];
    return [
      {
        externalId: cc.externalId,
        sourceAttribute: cc.sourceAttribute,
        operator: toConditionOperator(cc.operator),
        targetAttribute: cc.targetAttribute,
        isLiteralValue: cc.isLiteralValue ?? false,
        useTargetSchema: cc.useTargetSchema ?? false,
      },
    ];
  });

export {
  aggregateRuns,
  deriveWorstStatus,
  latestRunStatus,
} from '../consistencyAggregation';
export type { ReportStatus, RunAggregate } from '../consistencyAggregation';

export type ConsistencyReportSummary = {
  reportId: string;
  collectionId: string;
  collectionName: string;
  status: ReportStatus;
  runAt: string | number;
  schemas: string[];
  rulesCount: number;
  totalValid: number;
  totalFailed: number;
};

export type ConsistencyRuleRunResult = {
  runStatusId: string;
  status: ReportStatus;
  ruleId: string;
  ruleName: string;
  customerDefinedId: string | null;
  operator: RuleOperator;
  sourceSchemaFilterName: string;
  sourceAttribute: string;
  targetSchemaFilterName: string;
  targetAttribute: string;
  severity: RuleSeverity;
  errorMessage: string | null;
  sourceFilters: string | null;
  targetFilters: string | null;
  conditionChecks: ConditionCheckView[];
  totalValid: number;
  totalFailed: number;
};

export type FailedInstance = Record<string, unknown>;

export const REPORT_SORT_OPTIONS = [
  { name: 'Collection name (A-Z)', value: 'nameASC', icon: 'ArrowUp' },
  { name: 'Collection name (Z-A)', value: 'nameDESC', icon: 'ArrowDown' },
  { name: 'Run time (newest)', value: 'runTimeASC', icon: 'ArrowUp' },
  { name: 'Run time (oldest)', value: 'runTimeDESC', icon: 'ArrowDown' },
] as const;

export type ReportSortOption = (typeof REPORT_SORT_OPTIONS)[number]['value'];
